import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { ContentService, type TopicPublic } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import type { TopicQuestionRow } from "@/client/types.gen"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { AssignTopicsModal } from "@/components/topics/AssignTopicsModal"
import { QuestionPreviewModal } from "@/components/topics/QuestionPreviewModal"
import { Field } from "@/components/ui/field"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { toaster } from "@/components/ui/toaster"
import { useTopicQuestions } from "@/hooks/useTopicQuestions"
import { useUntaggedQuestions } from "@/hooks/useUntaggedQuestions"
import { handleError } from "@/utils"

const querySearchSchema = z.object({
  page: z.number().catch(1),
  q: z.string().optional(),
  untaggedPage: z.number().catch(1),
  untaggedSearch: z.string().optional(),
})

const QUESTIONS_PAGE_SIZE = 10
const UNTAGGED_PAGE_SIZE = 10

export const Route = createFileRoute("/_layout/admin-topics/$topicId")({
  component: AdminTopicDetailPage,
  validateSearch: (search) => querySearchSchema.parse(search),
})

function AdminTopicDetailPage() {
  const { topicId } = Route.useParams()
  const { page, q, untaggedPage, untaggedSearch } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const [questionSearch, setQuestionSearch] = useState(q ?? "")
  const [untaggedSearchInput, setUntaggedSearchInput] = useState(
    untaggedSearch ?? "",
  )
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(
    null,
  )
  const [assignQuestionId, setAssignQuestionId] = useState<string | null>(null)

  const { data: topic, isLoading: isTopicLoading } = useQuery<
    TopicPublic,
    ApiError
  >({
    queryKey: ["topic-detail", topicId],
    queryFn: () => ContentService.getTopic({ topicId }),
  })

  const questionsQuery = useTopicQuestions({
    topicId,
    page,
    search: q,
    pageSize: QUESTIONS_PAGE_SIZE,
  })

  const untaggedQuery = useUntaggedQuestions({
    page: untaggedPage,
    search: untaggedSearch,
    pageSize: UNTAGGED_PAGE_SIZE,
  })

  const removeQuestionMutation = useMutation({
    mutationFn: (questionId: string) =>
      ContentService.removeQuestionFromTopic({
        topicId,
        questionId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "topic-questions" &&
          query.queryKey[1] === topicId,
      })
      queryClient.invalidateQueries({ queryKey: ["untagged-questions"] })
    },
  })

  const addQuestionMutation = useMutation({
    mutationFn: (questionId: string) =>
      ContentService.addQuestionToTopic({
        topicId,
        questionId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "topic-questions" &&
          query.queryKey[1] === topicId,
      })
      queryClient.invalidateQueries({ queryKey: ["untagged-questions"] })
    },
  })

  const questions = (questionsQuery.data?.data ?? []) as TopicQuestionRow[]
  const questionCount = questionsQuery.data?.count ?? 0
  const untaggedQuestions = untaggedQuery.data?.data ?? []
  const untaggedCount = untaggedQuery.data?.count ?? 0

  const updateSearchParams = (
    updates: Partial<z.infer<typeof querySearchSchema>>,
  ) => {
    navigate({
      search: (prev: z.infer<typeof querySearchSchema>) => ({
        ...prev,
        ...updates,
      }),
    })
  }

  const handleRemoveFromTopic = async (questionId: string) => {
    try {
      await removeQuestionMutation.mutateAsync(questionId)
      toaster.success({
        title: "Question removed",
        description: "The question was removed from this topic.",
        action: {
          label: "Undo",
          onClick: () => {
            void addQuestionMutation
              .mutateAsync(questionId)
              .catch((err) => handleError(err as ApiError))
          },
        },
      })
    } catch (err) {
      handleError(err as ApiError)
    }
  }

  useEffect(() => {
    setQuestionSearch(q ?? "")
  }, [q])

  useEffect(() => {
    setUntaggedSearchInput(untaggedSearch ?? "")
  }, [untaggedSearch])

  const questionSection = useMemo(() => {
    if (questionsQuery.isLoading || isTopicLoading) {
      return (
        <Flex justify="center" py={6}>
          <Spinner />
        </Flex>
      )
    }

    if (questions.length === 0) {
      return (
        <AppEmptyState
          title={emptyStateCopy.topics.noQuestions.title}
          description={emptyStateCopy.topics.noQuestions.description}
          variant="compact"
          data-testid="topic-questions-empty-state"
        />
      )
    }

    return (
      <>
        {/* Desktop */}
        <Box display={{ base: "none", md: "block" }}>
          <Table.Root size={{ base: "sm", md: "md" }}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Question</Table.ColumnHeader>
                <Table.ColumnHeader w="30%">Topics</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {questions.map((question) => (
                <Table.Row key={question.question_id}>
                  <Table.Cell>
                    <HtmlContent
                      lineClamp={2}
                      content={question.question_text}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap={1} wrap="wrap">
                      {(question.topics ?? []).map((name) => (
                        <Badge key={name} colorPalette="green">
                          {name}
                        </Badge>
                      ))}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Flex gap={2} justify="flex-end" wrap="wrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setPreviewQuestionId(question.question_id)
                        }
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setAssignQuestionId(question.question_id)
                        }
                      >
                        Assign topics
                      </Button>
                      <Button
                        size="sm"
                        colorPalette="red"
                        variant="ghost"
                        onClick={() =>
                          handleRemoveFromTopic(question.question_id)
                        }
                        loading={
                          removeQuestionMutation.isPending &&
                          removeQuestionMutation.variables ===
                            question.question_id
                        }
                      >
                        Remove
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {/* Mobile cards */}
        <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
          {questions.map((question) => (
            <Card.Root key={question.question_id}>
              <Card.Body>
                <HtmlContent lineClamp={2} content={question.question_text} />
                {(question.topics ?? []).length > 0 && (
                  <Flex gap={1} wrap="wrap" mt={2}>
                    {(question.topics ?? []).map((name) => (
                      <Badge key={name} colorPalette="green" size="sm">
                        {name}
                      </Badge>
                    ))}
                  </Flex>
                )}
                <Flex gap={2} wrap="wrap" mt={3}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPreviewQuestionId(question.question_id)}
                  >
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAssignQuestionId(question.question_id)}
                  >
                    Assign topics
                  </Button>
                  <Button
                    size="sm"
                    colorPalette="red"
                    variant="ghost"
                    onClick={() => handleRemoveFromTopic(question.question_id)}
                    loading={
                      removeQuestionMutation.isPending &&
                      removeQuestionMutation.variables === question.question_id
                    }
                  >
                    Remove
                  </Button>
                </Flex>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      </>
    )
  }, [
    handleRemoveFromTopic,
    isTopicLoading,
    questions,
    questionsQuery.isLoading,
    removeQuestionMutation.isPending,
    removeQuestionMutation.variables,
  ])

  const untaggedSection = useMemo(() => {
    if (untaggedQuery.isLoading) {
      return (
        <Flex justify="center" py={6}>
          <Spinner />
        </Flex>
      )
    }

    if (untaggedQuestions.length === 0) {
      return (
        <AppEmptyState
          title={emptyStateCopy.topics.noUntaggedQuestions.title}
          description={emptyStateCopy.topics.noUntaggedQuestions.description}
          variant="compact"
          data-testid="topic-untagged-empty-state"
        />
      )
    }

    return (
      <>
        {/* Desktop */}
        <Box display={{ base: "none", md: "block" }}>
          <Table.Root size={{ base: "sm", md: "md" }}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Question</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Assign
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {untaggedQuestions.map((question) => (
                <Table.Row key={question.question_id}>
                  <Table.Cell>
                    <HtmlContent
                      lineClamp={2}
                      content={question.question_text}
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAssignQuestionId(question.question_id)}
                    >
                      Assign topics
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {/* Mobile cards */}
        <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
          {untaggedQuestions.map((question) => (
            <Card.Root key={question.question_id}>
              <Card.Body>
                <HtmlContent lineClamp={2} content={question.question_text} />
                <Flex justify="flex-end" mt={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAssignQuestionId(question.question_id)}
                  >
                    Assign topics
                  </Button>
                </Flex>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      </>
    )
  }, [untaggedQuery.isLoading, untaggedQuestions])

  if (isTopicLoading && !topic) {
    return (
      <Flex justify="center" py={16}>
        <Spinner />
      </Flex>
    )
  }

  if (!topic) {
    return (
      <Container maxW="4xl" py={12}>
        <AppEmptyState
          title={emptyStateCopy.topics.notFound.title}
          description={emptyStateCopy.topics.notFound.description}
          action={
            <Link to="/admin-topics">
              <Button>Back to topics</Button>
            </Link>
          }
          data-testid="topic-not-found-state"
        />
      </Container>
    )
  }

  return (
    <Container maxW="6xl" py={8}>
      <Link to="/admin-topics">
        <Button variant="ghost" gap={2} mb={4}>
          <FaArrowLeft />
          Back to topics
        </Button>
      </Link>
      <Heading size="lg" mb={2}>
        {topic.name}
      </Heading>
      <Text color="fg.muted" mb={6}>
        {questionCount} question{questionCount === 1 ? "" : "s"}
      </Text>

      <Box borderWidth="1px" borderRadius="lg" p={4} mb={8}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="md">Questions in this topic</Heading>
          <Field label="Search questions" width={{ base: "100%", md: "320px" }}>
            <InputGroup startElement={<FiSearch />}>
              <Input
                value={questionSearch}
                onChange={(event) => setQuestionSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    updateSearchParams({
                      q: questionSearch || undefined,
                      page: 1,
                    })
                  }
                }}
                placeholder="Search question text"
              />
            </InputGroup>
          </Field>
        </Flex>
        <Box mt={4}>{questionSection}</Box>
        <PaginationRoot
          page={page}
          count={questionCount}
          pageSize={QUESTIONS_PAGE_SIZE}
          onPageChange={({ page }) => updateSearchParams({ page })}
        >
          <Flex justify="flex-end" mt={4} gap={2}>
            <PaginationPrevTrigger asChild>
              <IconButton aria-label="Previous" variant="ghost" size="sm">
                <FaChevronLeft />
              </IconButton>
            </PaginationPrevTrigger>
            <PaginationItems />
            <PaginationNextTrigger asChild>
              <IconButton aria-label="Next" variant="ghost" size="sm">
                <FaChevronRight />
              </IconButton>
            </PaginationNextTrigger>
          </Flex>
        </PaginationRoot>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p={4}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="md">Questions without topics</Heading>
          <Field label="Search untagged" width={{ base: "100%", md: "320px" }}>
            <InputGroup startElement={<FiSearch />}>
              <Input
                value={untaggedSearchInput}
                onChange={(event) => setUntaggedSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    updateSearchParams({
                      untaggedSearch: untaggedSearchInput || undefined,
                      untaggedPage: 1,
                    })
                  }
                }}
                placeholder="Search untagged questions"
              />
            </InputGroup>
          </Field>
        </Flex>
        <Box mt={4}>{untaggedSection}</Box>
        <PaginationRoot
          page={untaggedPage}
          count={untaggedCount}
          pageSize={UNTAGGED_PAGE_SIZE}
          onPageChange={({ page }) =>
            updateSearchParams({ untaggedPage: page })
          }
        >
          <Flex justify="flex-end" mt={4} gap={2}>
            <PaginationPrevTrigger asChild>
              <IconButton aria-label="Previous" variant="ghost" size="sm">
                <FaChevronLeft />
              </IconButton>
            </PaginationPrevTrigger>
            <PaginationItems />
            <PaginationNextTrigger asChild>
              <IconButton aria-label="Next" variant="ghost" size="sm">
                <FaChevronRight />
              </IconButton>
            </PaginationNextTrigger>
          </Flex>
        </PaginationRoot>
      </Box>

      <QuestionPreviewModal
        questionId={previewQuestionId}
        isOpen={Boolean(previewQuestionId)}
        onClose={() => setPreviewQuestionId(null)}
      />
      <AssignTopicsModal
        questionId={assignQuestionId}
        isOpen={Boolean(assignQuestionId)}
        onClose={() => setAssignQuestionId(null)}
        defaultTopic={{ id: topic.id, name: topic.name }}
      />
    </Container>
  )
}

export default AdminTopicDetailPage
